import type {
  FeeLine,
  LoanProduct,
  RateExplanationRender,
} from "../../../lib/agent-types";
import { RateExplorerView } from "../../calculators/rate-explorer/rate-explorer-view";
import { WidgetFrame } from "../widget-frame";

export function RateExplanationWidget({
  payload,
}: {
  payload: RateExplanationRender;
}) {
  const primary = payload.estimate.results[0];
  const feeLines = primary?.raw.pricing.fee_lines ?? [];
  const llpaPoints =
    typeof primary?.raw.pricing.llpa_points === "number" &&
    primary.raw.pricing.llpa_points > 0
      ? primary.raw.pricing.llpa_points
      : null;
  const loanAmount = primary?.raw.payment_at_target_price?.loan_amount ?? 0;
  const product = primary?.raw.product;

  return (
    <WidgetFrame data-widget-type="rate_explanation">
      <div className="space-y-4">
        <RateExplorerView
          explanation={payload.explanation ?? null}
          executions={payload.lender_executions ?? []}
          pricingMode={payload.pricing_mode}
          pricingModeNote={payload.pricing_mode_note}
          modeledRatePct={payload.modeled_rate_pct}
        />
        <WidgetProductFeePanel
          feeLines={feeLines}
          llpaPoints={llpaPoints}
          loanAmountCents={loanAmount}
          product={product}
        />
      </div>
    </WidgetFrame>
  );
}

// WidgetProductFeePanel is the widget's slim mirror of the dashboard's
// ActiveProductFeePanel: same coherence story (program fee → actual
// number, LLPA → noop because it's already on the rate), shape adapted
// to the agent-widget payload which carries raw FeeLines rather than a
// pre-summarized DashboardRateWatchComparisonProduct.
function WidgetProductFeePanel({
  feeLines,
  llpaPoints,
  loanAmountCents,
  product,
}: {
  feeLines: FeeLine[];
  llpaPoints: number | null;
  loanAmountCents: number;
  product?: LoanProduct;
}) {
  const monthlyCents = feeLines
    .filter(isMonthlyFeeLine)
    .reduce((sum, line) => sum + line.amount, 0);
  const upfrontLines = feeLines.filter((line) => !isMonthlyFeeLine(line));
  const upfrontCents = upfrontLines.reduce((sum, line) => sum + line.amount, 0);
  const financedUpfrontCents = upfrontLines
    .filter((line) => line.financed)
    .reduce((sum, line) => sum + line.amount, 0);

  const hasFees = monthlyCents > 0 || upfrontCents > 0;
  if (hasFees) {
    const monthlyDollars = centsToRoundedDollars(monthlyCents);
    const upfrontDollars = centsToRoundedDollars(upfrontCents);
    const financedUpfrontDollars = centsToRoundedDollars(financedUpfrontCents);
    const label = productFeeLabel(product);

    const compact =
      monthlyDollars > 0
        ? upfrontDollars > 0
          ? `${formatCurrencyDollars(monthlyDollars)}/mo + ${formatCurrencyDollars(
              upfrontDollars,
            )}${financedUpfrontDollars > 0 ? " financed" : " upfront"}`
          : `${formatCurrencyDollars(monthlyDollars)}/mo`
        : `${formatCurrencyDollars(upfrontDollars)}${
            financedUpfrontDollars > 0 ? " financed" : " upfront"
          }`;
    const sub =
      monthlyDollars > 0 && upfrontDollars > 0
        ? `${label}: ${formatCurrencyDollars(upfrontDollars)}${
            financedUpfrontDollars > 0 ? " financed" : " upfront"
          } plus monthly charge.`
        : monthlyDollars > 0
          ? `${label} modeled as a monthly fee.`
          : `${label} modeled as an upfront${
              financedUpfrontDollars > 0 ? " financed" : ""
            } fee.`;
    return (
      <div className="rounded-xl border border-border-soft p-4">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          Program fee on this loan
        </p>
        <p className="mt-1 text-sm font-semibold tabular-nums text-foreground">
          {label} · {compact}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">{sub}</p>
      </div>
    );
  }
  if (llpaPoints !== null) {
    // LLPA lanes — decomposition table above already shows the adjustment
    // landing on the rate. Skip a redundant card.
    return null;
  }
  if (loanAmountCents <= 0) {
    return null;
  }
  return (
    <div className="rounded-xl border border-border-soft p-4">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        Program fees
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        No program fees on this lane.
      </p>
    </div>
  );
}

function isMonthlyFeeLine(line: FeeLine): boolean {
  if (typeof line.annual_rate === "number" && line.annual_rate > 0) {
    return true;
  }
  return /(_monthly|_annual_mip|_annual_fee|_funding_fee_monthly|pmi)$/i.test(
    line.kind ?? "",
  );
}

function productFeeLabel(product?: LoanProduct): string {
  switch (product) {
    case "fha":
      return "FHA MIP";
    case "va":
      return "VA funding fee";
    case "usda":
      return "USDA fee";
    default:
      return "Program fee";
  }
}

function centsToRoundedDollars(cents: number): number {
  return Math.round(cents / 100);
}

function formatCurrencyDollars(dollars: number): string {
  if (Math.abs(dollars) >= 1000) {
    return `$${(dollars / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  }
  return `$${dollars}`;
}
